import { AnimatePresence } from 'motion/react';
import { AnimatedSurface } from './Motion';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';

export function StorageDropdown<T extends string>({ label, value, options, onChange, icon, className = '', menuWidth = 180 }: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  icon: ReactNode;
  className?: string;
  menuWidth?: number;
}) {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const open = () => {
    const rect = trigger.current!.getBoundingClientRect();
    const height = options.length * 34 + 10;
    const width = Math.min(menuWidth, window.innerWidth - 16);
    setPosition({ width, left: Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8)),
      top: rect.bottom + height + 8 > window.innerHeight ? Math.max(8, rect.top - height - 4) : rect.bottom + 4 });
  };
  useEffect(() => {
    if (!position) return;
    menu.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus();
    const dismiss = (event: PointerEvent) => {
      if (!menu.current?.contains(event.target as Node) && !trigger.current?.contains(event.target as Node)) setPosition(null);
    };
    const close = () => setPosition(null);
    document.addEventListener('pointerdown', dismiss);
    window.addEventListener('resize', close);
    return () => { document.removeEventListener('pointerdown', dismiss); window.removeEventListener('resize', close); };
  }, [position]);
  return <div className={`storage-dropdown ${className}`}>
    <button ref={trigger} type="button" className="log-range__trigger" aria-label={`${label}: ${options.find(option => option.value === value)?.label}`}
      aria-haspopup="menu" aria-expanded={!!position} aria-controls={position ? id : undefined}
      onClick={() => position ? setPosition(null) : open()}
      onKeyDown={event => { if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); open(); } }}>
      {icon}<span>{options.find(option => option.value === value)?.label}</span><ChevronDown className="storage-dropdown__chevron" />
    </button>
    {createPortal(<AnimatePresence initial={false}>{position && <AnimatedSurface key="menu" origin={position.top < (trigger.current?.getBoundingClientRect().top ?? 0) ? "bottom right" : "top right"} ref={menu} id={id} role="menu" aria-label={label} className="log-range__menu storage-dropdown__menu" style={position}
      onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget) && !trigger.current?.contains(event.relatedTarget)) setPosition(null); }}
      onKeyDown={event => {
        if (event.key === 'Escape') { event.preventDefault(); setPosition(null); trigger.current?.focus(); }
        const buttons = Array.from(menu.current?.querySelectorAll<HTMLButtonElement>('button') ?? []);
        const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
        const next = event.key === 'ArrowDown' ? (index + 1) % buttons.length : event.key === 'ArrowUp' ? (index - 1 + buttons.length) % buttons.length : event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : -1;
        if (next >= 0) { event.preventDefault(); buttons[next]?.focus(); }
        if (event.key === 'Tab') { setPosition(null); trigger.current?.focus(); }
      }}>
      {options.map(option => <button key={option.value} type="button" role="menuitemradio" tabIndex={-1} aria-checked={option.value === value}
        onClick={() => { onChange(option.value); setPosition(null); trigger.current?.focus(); }}>
        <span>{option.label}</span>{option.value === value && <Check />}
      </button>)}
    </AnimatedSurface>}</AnimatePresence>, document.body)}
  </div>;
}
